import { useState, useCallback, useEffect } from "react";
import React from "react";
import { Button } from "@mui/material";
import SbuxInputSearch from "../SbuxInputSearch";
import SbuxTableFilters from "./SbuxTableFilters";
import SbuxBasicTable from "./SbuxBasicTable";
import Box from "@mui/material/Box";
import { getComparator } from "./utils";
import { TABLE_DEFAULT_PAGE_SIZE } from "../../constants";
import useTextTranslate from "../../features/content/hooks/useTextTranslate";
import styles from "./styles";
import useCss from "../../hooks/useCss";
import { searchDataset } from "../../utils/tableUtil";
import SbuxTranslate from "../SbuxTranslate";

type Order = "asc" | "desc";

type DataRecord = Record<string, unknown>;

interface Column {
  id: string;
  label: string;
  align?: "left" | "right" | "center";
  disablePadding?: boolean;
  minWidth?: number;
  sortable?: boolean;
  hide?: boolean;
  checkSelector?: boolean;
  colSpan?: number;
  [key: string]: unknown;
}

interface Props {
  rows?: DataRecord[];
  columns?: Column[];
  currentPage?: number;
  filterColumns?: Record<string, unknown>[] | null;
  filterTitle?: React.ReactNode;
  groupColumns?: Column[] | null;
  defaultSortingField?: string;
  defaultSortOrder?: Order;
  defaultRowsPerPage?: number | null;
  defaultFilterColumns?: string[];
  setFilteredItem?:
    | ((items: string[]) => void | { type: string; [key: string]: unknown })
    | null;
  RowSource?: React.ComponentType<{
    row: DataRecord;
    columns: Column[];
    handleEditRowClick: (row: DataRecord) => void;
    handleDeleteRowClick: (row: DataRecord) => void;
    handle24HoursClick: (row: DataRecord) => void;
    updatedRowId: string | null;
  }>;
  showAddNewButton?: boolean;
  showCustomButton?: boolean;
  showSubTabs?: boolean;
  SubTabs?: React.ComponentType | null;
  addNewButtonTitle?: React.ReactNode;
  addCustomButtonTitle?: React.ReactNode;
  showSearchBar?: boolean;
  showFooter?: boolean;
  showModalTable?: boolean;
  showRefreshButton?: boolean;
  isManager?: boolean;
  isLoading?: boolean;
  isdisableAddNewButton?: boolean;
  isTranslationRequired?: boolean;
  handleAddNewButtonClick?: (() => void) | null;
  handleEditRowClick?: ((row: DataRecord) => void) | null;
  handleDeleteRowClick?: ((row: DataRecord) => void) | null;
  handleRefreshClick?: (() => void) | null;
  handle24HoursClick?: ((row: DataRecord) => void) | null;
  handleCustomButtonClick?: (() => void) | null;
  updatedRowId?: string | null;
  handleCheckboxChange?: (event: React.ChangeEvent<HTMLInputElement>) => void;
  selectionStrategy?: Record<string, unknown> | null;
  isCustomButtonDisabled?: boolean;
  isTableDisabled?: boolean;
  onRowsPerPageChange?:
    | ((
        event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
      ) => void)
    | null;
  onPageChange?: ((event: unknown, newPage: number) => void) | null;
  onRequestSort?: ((event: React.MouseEvent, property: string) => void) | null;
  onDataFilterChange?: ((filters: Record<string, unknown>) => void) | null;
  footerMarginBottom?: string;
}

const SbuxEnhancedTable = ({
  rows = [],
  columns = [],
  currentPage = 0,
  filterColumns = null,
  filterTitle = null,
  groupColumns = null,
  defaultSortingField = "",
  defaultSortOrder = "desc",
  defaultRowsPerPage = null,
  defaultFilterColumns = [],
  setFilteredItem,
  RowSource,
  showAddNewButton = false,
  showCustomButton = false,
  showSubTabs = false,
  SubTabs = null,
  addNewButtonTitle = "",
  addCustomButtonTitle = "",
  showSearchBar = true,
  showFooter = true,
  showModalTable = false,
  showRefreshButton = false,
  isManager = false,
  isLoading = false,
  isdisableAddNewButton = false,
  isTranslationRequired = true,
  handleAddNewButtonClick = null,
  handleEditRowClick = null,
  handleDeleteRowClick = null,
  handleRefreshClick = null,
  handle24HoursClick = null,
  handleCustomButtonClick = null,
  updatedRowId = null,
  handleCheckboxChange,
  selectionStrategy = null,
  isCustomButtonDisabled = false,
  isTableDisabled = false,
  onRowsPerPageChange = null,
  onPageChange = null,
  onRequestSort = null,
  onDataFilterChange = null,
  footerMarginBottom,
}: Props) => {
  const classes = useCss(styles);
  const [rowData, setRowData] = useState<DataRecord[]>([]);
  const [order, setOrder] = useState<Order>(defaultSortOrder as Order);
  const [orderBy, setOrderBy] = useState(defaultSortingField);
  const [page, setPage] = useState(currentPage ?? 0);
  const [rowsPerPage, setRowsPerPage] = useState(
    defaultRowsPerPage === null ? TABLE_DEFAULT_PAGE_SIZE : defaultRowsPerPage,
  );
  const [searchText, setSearchText] = useState("");

  const translatedSearchPlaceHolder = useTextTranslate("Search");

  useEffect(() => {
    setRowData(Array.isArray(rows) ? rows : []);
    searchText !== "" && handleInputChange(searchText);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rows]);

  useEffect(() => {
    const rowsArray = Array.isArray(rows) ? rows : [];
    const filterData = rowsArray.reduce<DataRecord[]>((acc, cur) => {
      const curValues = Object.values(cur);
      const isExists = curValues?.some((f) =>
        defaultFilterColumns?.includes(f as string),
      );
      if (isExists) {
        acc.push(cur);
      }
      return acc;
    }, []);
    if (!!filterData?.length) {
      setRowData(filterData);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [defaultFilterColumns]);

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
    typeof onPageChange === "function" && onPageChange(event, newPage);
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    handleChangePage(event, 0);
    typeof onRowsPerPageChange === "function" && onRowsPerPageChange(event);
  };

  const handleRequestSort = useCallback(
    (event: React.MouseEvent, property: string) => {
      const isAsc = orderBy === property && order === "asc";
      setOrder(isAsc ? "desc" : "asc");
      setOrderBy(property);
      typeof onRequestSort === "function" && onRequestSort(event, property);
    },
    [order, orderBy, onRequestSort],
  );

  const handleInputChange = useCallback(
    (searchValue: string) => {
      setSearchText(searchValue);

      if (typeof onDataFilterChange === "function") {
        onDataFilterChange({ searchText: searchValue });
        handleChangePage({}, 0);
      } else {
        const rowsArray = Array.isArray(rows) ? rows : [];
        const filterRows = searchDataset(columns, rowsArray, searchValue);
        handleChangePage({}, 0);
        setRowData(filterRows as DataRecord[]);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [rows, onDataFilterChange],
  );

  const BodySource = () => {
    let rowsSlice = rowData;

    if (rowsPerPage > 0) {
      if (!onRequestSort) {
        rowsSlice = rowData.slice().sort(getComparator(order, orderBy));
      }
      rowsSlice = rowsSlice.slice(
        page * rowsPerPage,
        page * rowsPerPage + rowsPerPage,
      );
    }

    return (
      <>
        {rowsSlice &&
          rowsSlice.map((row, index) => {
            if (!RowSource) return null;
            // Use row.id if available for stable keys, fall back to index
            const rowKey = row.id !== undefined ? row.id : index;
            return (
              <RowSource
                key={rowKey}
                row={row}
                columns={columns}
                handleEditRowClick={(r) =>
                  handleEditRowClick && handleEditRowClick(r)
                }
                handleDeleteRowClick={(r) =>
                  handleDeleteRowClick && handleDeleteRowClick(r)
                }
                handle24HoursClick={(r) =>
                  handle24HoursClick && handle24HoursClick(r)
                }
                updatedRowId={updatedRowId}
              />
            );
          })}
      </>
    );
  };

  return (
    <Box
      className={
        isTableDisabled ? classes.disabledContainer : classes.enabledContainer
      }
    >
      {showSearchBar && (
        <Box className={classes.searchBarContainer}>
          {showSubTabs && SubTabs && (
            <Box className="tabs">
              <SubTabs />
            </Box>
          )}
          <Box className={classes.actionContainer}>
            {showAddNewButton && (
              <Button
                variant="contained"
                size="medium"
                onClick={handleAddNewButtonClick ?? undefined}
                disabled={isTableDisabled || isdisableAddNewButton}
              >
                {addNewButtonTitle}
              </Button>
            )}
            {showRefreshButton && (
              <Button
                variant="contained"
                size="medium"
                onClick={handleRefreshClick ?? undefined}
                disabled={isTableDisabled}
              >
                {<SbuxTranslate>{"Refresh"}</SbuxTranslate>}
              </Button>
            )}
            {showCustomButton && (
              <Button
                variant="contained"
                size="small"
                onClick={handleCustomButtonClick ?? undefined}
                disabled={isTableDisabled || isCustomButtonDisabled}
              >
                {addCustomButtonTitle}
              </Button>
            )}
            <SbuxInputSearch
              value={searchText}
              translatedSearchPlaceHolder={
                isTranslationRequired ? translatedSearchPlaceHolder : "Search"
              }
              handleChange={(event) => handleInputChange(event.target.value)}
              handleClear={() => handleInputChange("")}
            />
            {filterColumns && setFilteredItem && (
              <SbuxTableFilters
                columns={filterColumns}
                title={filterTitle}
                defaultFilterColumns={defaultFilterColumns}
                setFilteredItem={setFilteredItem}
              />
            )}
          </Box>
        </Box>
      )}
      <SbuxBasicTable
        BodySource={BodySource}
        headerGroupCells={groupColumns}
        headerCells={columns}
        rowsPerPage={rowsPerPage}
        defaultRowsPerPage={defaultRowsPerPage ?? undefined}
        rows={rowData}
        page={page}
        order={order}
        orderBy={orderBy}
        showFooter={showFooter}
        selectionStrategy={selectionStrategy}
        showModalTable={showModalTable}
        onRequestSort={handleRequestSort}
        handleChangePage={handleChangePage}
        handleChangeRowsPerPage={handleChangeRowsPerPage}
        isManager={isManager}
        isLoading={isLoading}
        isTranslationRequired={isTranslationRequired}
        handleRefreshClick={handleRefreshClick ?? (() => {})}
        handleCheckboxChange={handleCheckboxChange}
        footerMarginBottom={footerMarginBottom}
      />
    </Box>
  );
};

export default SbuxEnhancedTable;
